<!-- TODO: this component needs a deep refactor -->
<template>
  <div>
    <v-row align="center" no-gutters class="mt-2 mb-4">
      <v-col id="dados-de-pagamento" cols="auto" class="mr-2">
        Resumo do Pedido
      </v-col>
      <v-col>
        <v-divider color="primary" thickness="1" :opacity="0.7" />
      </v-col>
    </v-row>

    <display-adapter>
      <template #mobile>
        <payment-summary-mobile :sectors="filteredSectors" :plan="plan" />
      </template>
      <template #desktop>
        <payment-summary-desktop :sectors="filteredSectors" :plan="plan" />
      </template>
    </display-adapter>

    <div v-if="!isZeroValue">
      <v-row align="center" no-gutters class="mt-6">
        <v-col id="dados-de-pagamento" cols="auto" class="mr-2">
          Dados de Pagamento
        </v-col>
        <v-col>
          <v-divider color="primary" thickness="1" :opacity="0.7" />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <t-alert class="mb-8" color="grey-lighten-2">
            <b>FIQUE ATENTO!</b> SEU VOUCHER (E-TICKET) ESTARÁ DISPONÍVEL EM ATÉ 15 MINUTOS APÓS A CONFIRMAÇÃO DO SEU
            PAGAMENTO EM NOSSO SITE. CASO ESTE TEMPO SEJA EXCEDIDO E VOCÊ NÃO SEJA REDIRECIONADO DE FORMA AUTOMÁTICA,
            CONSULTE NA ABA MEUS INGRESSOS OU ENTRE EM CONTATO COM A NOSSA CENTRAL DE ATENDIMENTO.
          </t-alert>
        </v-col>
      </v-row>
    </div>
    <v-expansion-panels v-model="selected" class="mt-4" flat>
      <v-expansion-panel
        v-for="method in methods"
        :key="method.id"
        class="custom-expansion-panel bg-background"
        style="border-bottom: 1px solid #ccc"
      >
        <v-expansion-panel-title>
          <template #default>
            <div style="display: flex; align-items: center">
              <img
                v-if="method.name == 'PIX'"
                :id="method.icon"
                class="pa-2"
                width="40px"
                :src="pixLogo"
                alt=""
              >
              <i v-else :class="method.icon + ' pa-2 text-primary'" style="font-size: 24px" />
              <span> {{ method.name }} </span>
            </div>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <component
            :is="method.component"
            :ref="method.key"
            :method="method.model"
            :loading="loading"
            @submit="emit('next')"
            @expired="redirectExpired"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, type Component, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useSnackbar } from "@/stores/snackbar";
import TAlert from "@/components/core/TAlert.vue";
import CheckoutResult from "@/types/CheckoutResult";
import CreditCardForm from "@/components/Ticket/PaymentForms/CreditCardForm.vue";
import PixForm from "@/components/Ticket/PaymentForms/PixForm.vue";
import Order, { OrderStatus } from "@/types/Order";
import PixPayment from "@/types/PixPayment";
import getOrderDetails from "@/api/requests/getOrderDetails";
import Event from "@/types/Event";
import { TrackableEvent } from "@/analytics/TrackableEvents";
import PixLogo from "@/assets/icons/pix.svg";
import ZeroValueTicketForm from "@/components/Ticket/PaymentForms/ZeroValueTicketForm.vue";
import PaymentMethod, { PaymentMethodKey } from "@/types/PaymentMethod";
import { useAnalytics } from "@/plugins/analytics";
import { useEventsStore } from "@/stores/events";
import { useCheckoutStore } from "@/stores/checkout";
import { useCartStore } from "@/stores/cart";
import { useOrdersStore } from "@/stores/orders";
import PaymentSummaryDesktop from "@/components/payment/details/desktop/PaymentSummary.vue";
import PaymentSummaryMobile from "@/components/payment/details/mobile/PaymentSummary.vue";
import Sector from "@/types/Sector";
import InstallmentPlan from "@/types/InstallmentPlan";

interface Form {
  validate: () => Promise<boolean>;
  pay: () => Promise<CheckoutResult>;
};

interface Method {
  id: number;
  name: string;
  key: "credit-card" | "pix" | "zero-value";
  icon: string;
  component: Component;
  condition(): boolean;
  model?: PaymentMethod;
}

const emit = defineEmits<{
  next: [];
}>();

const props = defineProps<{
  sectors: Sector[];
}>();

const router = useRouter();
const snackbar = useSnackbar();
const cart = useCartStore();
const checkout = useCheckoutStore();
const instance = getCurrentInstance();

const selected = ref(0);
const loading = ref(false);
const order = ref<Order>({} as Order);

const event = computed((): Event | null => useEventsStore().selected);
const payment = computed((): PixPayment => checkout.transaction as PixPayment);
const total = computed((): number => cart.totalAmount);
const isZeroValue = computed((): boolean => total.value === 0);
const pixLogo = PixLogo;
let interval: number | null = null;

const filteredSectors = computed((): Sector[] => {
  return props.sectors.filter((sector) => {
    return sector.sections.some((section) => {
      return section.tickets.reduce((acc, t) => acc + t.quantidade, 0) > 0;
    });
  });
});

const methods = computed((): Method[] => {
  if (event.value === null) return [];

  const _methods: Method[] = [
    {
      id: 5,
      name: "PIX",
      icon: "",
      key: "pix",
      component: PixForm,
      condition: () => !isZeroValue.value,
      model: event.value.payment_methods.find(pm => pm.id === PaymentMethodKey.Pix),
    },
    {
      id: 1,
      name: "Cartão de Crédito",
      icon: "mdi mdi-credit-card",
      key: "credit-card",
      component: CreditCardForm,
      condition: () => payment.value === null && !isZeroValue.value,
      model: event.value.payment_methods.find(pm => pm.id === PaymentMethodKey.CreditCard),
    },
    {
      id: 7,
      name: "Valor Isento",
      icon: "mdi mdi-thumb-up",
      key: "zero-value",
      component: ZeroValueTicketForm,
      condition: () => payment.value === null && isZeroValue.value,
      model: event.value.payment_methods.find(pm => pm.id === PaymentMethodKey.ZeroValue),
    },
  ];

  return _methods.filter(m => m.condition() && m.model !== undefined);
});

const currentMethod = computed(() => methods.value[selected.value]);

/** The Installment plan the buyer chose, while paying by card. */
const plan = computed<InstallmentPlan | null>(() => {
  return currentMethod.value?.key === "credit-card" ? checkout.plan : null;
});

async function validate(): Promise<boolean> {
  if (instance?.refs?.[currentMethod.value.key] === undefined) {
    throw new Error("Método de pagamento inválido!");
  }

  const candidates = instance.refs[currentMethod.value.key] as unknown[];
  const form = candidates[0] as Form;

  if (!(await form.validate())) {
    throw new Error("Erro de validação do formulário");
  }

  const reservation = checkout.reservation;

  if (reservation === null) {
    router.back();
    throw new Error("Sua reserva expirou!");
  }

  return true;
}

async function pay(): Promise<CheckoutResult> {
  if (instance?.refs?.[currentMethod.value.key] === undefined) {
    throw new Error("Método de pagamento inválido!");
  }

  const candidates = instance.refs[currentMethod.value.key] as unknown[];
  const form = candidates[0] as Form;

  return await form.pay();
}

async function submit() {
  checkout.timer.stop();

  let result: CheckoutResult;

  try {
    result = await pay();
  } catch (error) {
    // A failed payment leaves the Order payable while its Reservation lasts, so the countdown carries on
    checkout.timer.resume();
    throw error;
  }

  order.value = result.data.order;

  switch (currentMethod.value.key) {
    case "pix":
      checkout.transaction = result.data.payment;
      interval = setInterval(checkOrderStatus, 15000);
      break;
    // Other payment methods are sync, redirect immediately
    default:
      redirect();
      break;
  }
}

function redirectExpired() {
  cart.reset();
  checkout.reset();
  router.push({
    path: "/ticket/payments",
    query: { status: "Recusado" },
  });
}

function redirect(): void {
  const message = currentMethod.value.key === "zero-value"
    ? "Ingresso resgatado com sucesso!"
    : "Pagamento confirmado com sucesso!";

  snackbar.show(message, "success");

  useOrdersStore().select(order.value);

  cart.reset();
  checkout.reset();

  useAnalytics().collect(
    TrackableEvent.Purchase,
    {
      producer_id: event.value!.cliente_id,
      params: {
        currency: "BRL",
        value: order.value.valor_total,
        transaction_id: order.value.id,
        items: order.value.items.map(item => ({
          item_id: item.id,
          item_name: item.tipo,
          price: item.valor,
          quantity: item.quantidade,
          item_category: item.nome_cortesia ? "Cortesia" : "Normal",
          item_variant: order.value.evento_id,
        })),
      },
    },
  );

  router.push(`/ticket/list/${order.value.id}`);
}

async function checkOrderStatus() {
  try {
    const orderData = await getOrderDetails(order.value.id);

    if (orderData.situacao !== OrderStatus.CONFIRMED) {
      return;
    }

    clearInterval(interval!);
    order.value = orderData;
    redirect();
  } catch (error) {
    snackbar.show(error as string, "error");
  }
}

onBeforeUnmount(() => {
  if (interval !== null) {
    clearInterval(interval);
  }
});

defineExpose({
  validate,
  submit,
  pay,
});
</script>

<style>
td {
  border-bottom: none !important;
  height: 40px !important;
}
td.td-border {
  border-bottom: 1px solid #ccc !important;
  border-top: 1px solid #ccc !important;
}
.v-expansion-panel--active:not(:first-child),
.v-expansion-panel--active + .v-expansion-panel {
  margin-top: 0px !important;
}

.table-header {
  font-size: 1rem !important;
}
</style>
